// CatalogEntry.returns(CatalogField[], 중첩 fields)를 노드 바인딩 select에 쓸
// dot-path 목록으로 평탄화한다. 중간 객체/배열 노드도, 그 하위 leaf도 모두 선택지로 포함.
//   organization → 'organization', organization.name → 'organization.name'
//   findings(array) → 'findings', findings.category → 'findings.category'
// 라벨은 부모 라벨을 ' / '로 join한다('조직 / 사업장명').

import type { CatalogEntry, CatalogField, ValueSetId } from '@safetyhub/shared';

export interface FlatPath {
  path: string;
  label: string;
  type: string;
}

function walk(
  fields: CatalogField[],
  parentPath: string,
  parentLabel: string,
  out: FlatPath[],
): void {
  for (const f of fields) {
    const path = parentPath ? `${parentPath}.${f.path}` : f.path;
    const label = parentLabel ? `${parentLabel} / ${f.label}` : f.label;
    out.push({ path, label, type: f.type });
    if (f.fields && f.fields.length > 0) {
      walk(f.fields, path, label, out);
    }
  }
}

export function flattenCatalogPaths(returns: CatalogField[]): FlatPath[] {
  const out: FlatPath[] = [];
  walk(returns, '', '', out);
  return out;
}

// ============================================================
// 트리(드릴다운) select picker용 순수 헬퍼
// ============================================================
//
// 엔진(getByPath, Phase 1 dc5296125)이 지원하는 select 문법을 구조화한다.
//   일반 키:   a.b.c
//   인덱스:    categories.0.count          (단독 숫자 세그먼트)
//   조건:      categories[key=office].count (배열 필드에서 key=값 매칭한 첫 항목)
//   선행필드 없는 조건: [periodType=current].categories[key=office].count
//     (현재 값 자체가 배열일 때, 예: cardinality='list' 엔트리의 최상위 배열)
// 세그먼트 조건 정규식은 엔진 resolver.ts와 동일하게 유지한다.

const CONDITION_SEGMENT_RE = /^([^[\]]*)\[([^=[\]]+)=([^[\]]*)\]$/;

/** select 경로 1세그먼트의 구조화 표현. */
export type PathSegment =
  // 일반 키 (a)
  | { kind: 'field'; name: string }
  // 배열 인덱스 접근 (a.0 → field 'a' 다음의 index, name=''). 최상위 list 인덱스도 name=''.
  | { kind: 'index'; name: string; index: number }
  // 배열 조건 접근 (a[k=v]). name=''이고 직전 field 세그먼트가 배열명을 가짐.
  //   선행필드 없는 조건([k=v], 현재 값이 배열)도 name=''.
  | { kind: 'condition'; name: string; condKey: string; condVal: string };

/**
 * select 문자열을 PathSegment[]로 파싱한다.
 *
 * 배열 진입은 `field`(배열명) + `index`/`condition`(name='') 2세그먼트로 표현한다.
 * 예: 'categories[key=office]' → [field 'categories', condition('', key, office)]
 *     선행필드 없는 '[periodType=current]' → [condition('', periodType, current)]
 */
export function parseSelectPath(select: string | undefined): PathSegment[] {
  if (!select) return [];
  const out: PathSegment[] = [];
  for (const raw of select.split('.')) {
    const cond = raw.match(CONDITION_SEGMENT_RE);
    if (cond) {
      const field = cond[1];
      if (field !== '') out.push({ kind: 'field', name: field });
      out.push({ kind: 'condition', name: '', condKey: cond[2], condVal: cond[3] });
      continue;
    }
    if (/^\d+$/.test(raw)) {
      out.push({ kind: 'index', name: '', index: Number(raw) });
      continue;
    }
    out.push({ kind: 'field', name: raw });
  }
  return out;
}

/**
 * PathSegment[]를 엔진이 이해하는 select 문자열로 직렬화한다(parseSelectPath의 역).
 *
 * 직렬화 규칙(round-trip 보장):
 *   - field → 새 토큰 `name`
 *   - index(name='') → 새 토큰 `${index}` (`.`-join → 'categories.0')
 *   - condition(name='') → 직전 field 토큰에 suffix 부착 ('categories' → 'categories[k=v]'),
 *     단 토큰이 없으면(최상위 조건) 단독 토큰 '[k=v]'
 * 토큰들을 `.`로 join한다.
 */
export function buildSelectPath(segments: PathSegment[]): string {
  const tokens: string[] = [];
  segments.forEach((seg, i) => {
    if (seg.kind === 'field') {
      tokens.push(seg.name);
      return;
    }
    if (seg.kind === 'index') {
      tokens.push(String(seg.index));
      return;
    }
    // condition
    const suffix = `[${seg.condKey}=${seg.condVal}]`;
    const prev = segments[i - 1];
    if (seg.name === '' && prev && prev.kind === 'field' && tokens.length > 0) {
      tokens[tokens.length - 1] += suffix;
    } else if (seg.name !== '') {
      tokens.push(`${seg.name}${suffix}`);
    } else {
      tokens.push(suffix);
    }
  });
  return tokens.join('.');
}

/**
 * catalogKey/select가 가리키는 최종 CatalogField(leaf)를 반환한다. field 세그먼트만 따라
 * 내려가고 index/condition(배열 항목 지정)은 요소 필드로 진입한다. 조건 빌더의 값 입력이
 * enum(valueSetId)인지 판정하는 데 쓴다. 경로가 카탈로그를 벗어나거나 최종이 필드가 아니면
 * (배열/객체 진입 직후 등) undefined.
 *
 * cardinality='list' 엔트리는 resolve 값 자체가 배열이라 returns가 요소 필드다 →
 * 시작 fields=entry.returns가 요소 필드로 바로 동작한다.
 */
export function resolveSelectField(
  entry: CatalogEntry,
  select: string | undefined,
): CatalogField | undefined {
  const segments = parseSelectPath(select);
  let fields: CatalogField[] = entry.returns;
  let current: CatalogField | undefined;
  for (const seg of segments) {
    if (seg.kind === 'field') {
      const child = fields.find((f) => f.path === seg.name);
      if (!child) return undefined;
      current = child;
      fields = child.fields ?? [];
    } else {
      // index/condition → 배열 요소로 진입: 현재 배열 필드의 요소 필드로 전환.
      fields = current?.fields ?? fields;
      current = undefined;
    }
  }
  return current;
}

// --- 카탈로그 트리 walk (현재 노드 형상 산출) ---

/** enum 필드면 참조 value-set id, 아니면 undefined. */
function valueSetIdOf(f: CatalogField): ValueSetId | undefined {
  return f.type === 'enum' ? f.valueSetId : undefined;
}

type Shape =
  | { type: 'array'; fields: CatalogField[] } // 요소 형상=fields
  | { type: 'object'; fields: CatalogField[] }
  | { type: 'scalar' };

function shapeForField(f: CatalogField): Shape {
  if (f.type === 'array') return { type: 'array', fields: f.fields ?? [] };
  if (f.type === 'object') return { type: 'object', fields: f.fields ?? [] };
  return { type: 'scalar' };
}

function rootShape(entry: CatalogEntry): Shape {
  // cardinality='list'면 resolve 값 자체가 배열(요소 형상=returns).
  return entry.cardinality === 'list'
    ? { type: 'array', fields: entry.returns }
    : { type: 'object', fields: entry.returns };
}

interface WalkResult {
  /** 마지막 세그먼트까지 따라간 뒤의 현재 노드 형상 */
  current: Shape;
  /** 세그먼트별 사람용 라벨(breadcrumb용) */
  labels: string[];
}

/** segments를 카탈로그 트리에 따라 내려가며 현재 형상 + 라벨을 산출. */
function walkSegments(entry: CatalogEntry, segments: PathSegment[]): WalkResult {
  let current = rootShape(entry);
  const labels: string[] = [];
  for (const seg of segments) {
    if (seg.kind === 'field') {
      if (current.type !== 'object') {
        labels.push(seg.name);
        current = { type: 'scalar' };
        continue;
      }
      const child = current.fields.find((f) => f.path === seg.name);
      labels.push(child?.label ?? seg.name);
      current = child ? shapeForField(child) : { type: 'scalar' };
      continue;
    }
    // index / condition → 배열에서 항목 선택 → 요소(객체) 형상으로 전환
    const elemFields = current.type === 'array' ? current.fields : [];
    if (seg.kind === 'index') {
      labels.push(`${seg.index + 1}번째`);
    } else {
      const keyLabel = elemFields.find((f) => f.path === seg.condKey)?.label ?? seg.condKey;
      labels.push(`${keyLabel}=${seg.condVal}`);
    }
    current = elemFields.length > 0 ? { type: 'object', fields: elemFields } : { type: 'scalar' };
  }
  return { current, labels };
}

/** breadcrumb/풀이 표시에 쓸, 세그먼트별 한글 라벨. */
export function catalogSegmentLabels(entry: CatalogEntry, segments: PathSegment[]): string[] {
  return walkSegments(entry, segments).labels;
}

// ============================================================
// 펼친 트리(expanded-tree) picker용 순수 헬퍼 (Phase 3 UI 재설계)
// ============================================================
//
// 진행형 드릴다운(catalogLevelView, 제거됨) 대신 반환 구조 전체를 한 번에
// 트리로 펼친다. 트리에서 leaf(스칼라/enum) 또는 배열 노드(=나열) 하나를
// 단일 선택하고, 경로 중간의 배열은 그 노드에서 인라인으로 항목(인덱스/조건)을
// 지정한다. 직렬화는 기존 parseSelectPath/buildSelectPath 문법을 재사용한다.
// 결정 기록: docs/decisions/2026-06-28-composer-binding-picker-expanded-tree.md

/** 루트 노드의 구조 키(sentinel). cardinality='list'면 최상위 배열, 'single'이면 객체. */
export const ROOT_KEY = '$root';

/** 펼친 트리의 노드. `key`는 itemSpecs·선택 상태를 식별하는 구조 경로(항목지정 무관). */
export interface CatalogTreeNode {
  /** 구조 경로 키. 루트=ROOT_KEY, 루트 직속 자식='path', 그 하위='parent.path' */
  key: string;
  /** select 세그먼트 이름(=field.path). 루트는 '' */
  segName: string;
  label: string;
  type: CatalogField['type'];
  isArray: boolean;
  isObject: boolean;
  /** 스칼라/enum(더 내려갈 자식 없음) */
  isLeaf: boolean;
  /** enum이면 값 집합 id(조건 값 드롭다운용) */
  valueSetId?: ValueSetId;
  children: CatalogTreeNode[];
}

/**
 * 키-값 맵 형태 배열의 맵 뷰 정보.
 *   keyField   = 요소를 식별하는 enum 키 필드(valueSetId 보유, 정확히 1개)
 *   valueFields = 그 외 스칼라 값 필드(들). 1개면 자동 출력, 여러 개면 값 필드 선택.
 */
export interface MapArrayInfo {
  keyField: CatalogTreeNode;
  valueFields: CatalogTreeNode[];
}

/**
 * 배열 요소가 'enum 키 1개 + 값 필드 ≥1' 형태(예 categories=[{key,count}])면 맵 뷰
 * 정보를, 아니면 null을 반환한다. enum 키는 value-set 선언(SSOT)으로 식별한다.
 *   - 동적 값 배열(periodValue만 있는 연도별 배열 등) → enum 키 없음 → null
 *   - enum 키만 있고 값 leaf가 없는 배열 → null
 */
export function mapArrayInfo(node: CatalogTreeNode): MapArrayInfo | null {
  if (!node.isArray) return null;
  const leaves = node.children.filter((c) => c.isLeaf);
  const enumKeys = leaves.filter((c) => !!c.valueSetId);
  if (enumKeys.length !== 1) return null; // 정확히 1개 enum 키만
  const keyField = enumKeys[0];
  const valueFields = leaves.filter((c) => c !== keyField);
  if (valueFields.length === 0) return null; // 값 필드 없으면 맵 아님
  return { keyField, valueFields };
}

/** 배열 노드의 항목 지정 방식. */
export type ArrayItemSpec =
  | { mode: 'index'; index: number }
  | { mode: 'condition'; condKey: string; condVal: string };

/** 펼친 트리 선택 상태: 최종 선택 노드 + 통과하는 배열별 항목 지정. */
export interface TreeState {
  /** 선택된 노드의 구조 키(leaf 또는 배열 노드=나열, 루트=전체). */
  targetKey: string;
  /** 배열 노드 key → 항목 지정(없으면 통과 시 index 0 기본). */
  itemSpecs: Record<string, ArrayItemSpec>;
}

function buildTreeNode(f: CatalogField, parentKey: string): CatalogTreeNode {
  const key = parentKey === ROOT_KEY ? f.path : `${parentKey}.${f.path}`;
  const shape = shapeForField(f);
  const isArray = shape.type === 'array';
  const isObject = shape.type === 'object';
  const children =
    shape.type === 'scalar' ? [] : shape.fields.map((c) => buildTreeNode(c, key));
  return {
    key,
    segName: f.path,
    label: f.label,
    type: f.type,
    isArray,
    isObject,
    isLeaf: shape.type === 'scalar',
    valueSetId: valueSetIdOf(f),
    children,
  };
}

/**
 * CatalogEntry를 단일 루트 트리로 펼친다(picker가 한 번에 렌더).
 *
 * cardinality='list'면 루트=최상위 배열 노드(자식=요소 fields),
 * 'single'이면 루트=객체 노드(자식=returns)다. 항상 길이 1 배열을 반환한다.
 */
export function buildCatalogTree(entry: CatalogEntry): CatalogTreeNode[] {
  const isList = entry.cardinality === 'list';
  const children = entry.returns.map((f) => buildTreeNode(f, ROOT_KEY));
  return [
    {
      key: ROOT_KEY,
      segName: '',
      label: entry.label,
      type: isList ? 'array' : 'object',
      isArray: isList,
      isObject: !isList,
      isLeaf: false,
      valueSetId: undefined,
      children,
    },
  ];
}

function specToSegment(spec: ArrayItemSpec): PathSegment {
  return spec.mode === 'index'
    ? { kind: 'index', name: '', index: spec.index }
    : { kind: 'condition', name: '', condKey: spec.condKey, condVal: spec.condVal };
}

function segmentToSpec(seg: PathSegment): ArrayItemSpec | undefined {
  if (seg.kind === 'index') return { mode: 'index', index: seg.index };
  if (seg.kind === 'condition') {
    return { mode: 'condition', condKey: seg.condKey, condVal: seg.condVal };
  }
  return undefined;
}

/** 루트부터 targetKey 노드까지의 조상 체인(루트 포함, target 포함)을 찾는다. */
function findChain(nodes: CatalogTreeNode[], targetKey: string): CatalogTreeNode[] | null {
  for (const node of nodes) {
    if (node.key === targetKey) return [node];
    const sub = findChain(node.children, targetKey);
    if (sub) return [node, ...sub];
  }
  return null;
}

/**
 * 트리 선택 상태(targetKey + itemSpecs)를 엔진 select 문자열로 직렬화.
 * - 루트(=ROOT_KEY) 선택 → '' (전체 바인딩, select 없음)
 * - 통과하는 배열: itemSpecs의 항목 지정(없으면 index 0) 부착
 * - target 배열(나열): field만, 항목 지정 없음
 */
export function treeStateToSelect(
  entry: CatalogEntry,
  targetKey: string,
  itemSpecs: Record<string, ArrayItemSpec>,
): string | undefined {
  const tree = buildCatalogTree(entry);
  const chain = findChain(tree, targetKey) ?? tree; // 미발견 시 루트(전체)
  const segments: PathSegment[] = [];
  for (const node of chain) {
    const isTarget = node.key === chain[chain.length - 1].key;
    if (node.key === ROOT_KEY) {
      // 루트 자신이 target이면 전체 바인딩(세그먼트 없음).
      if (!isTarget && node.isArray) {
        segments.push(specToSegment(itemSpecs[ROOT_KEY] ?? { mode: 'index', index: 0 }));
      }
      continue; // 루트는 field 세그먼트를 만들지 않음(객체 루트는 통과만)
    }
    segments.push({ kind: 'field', name: node.segName });
    if (node.isArray && !isTarget) {
      segments.push(specToSegment(itemSpecs[node.key] ?? { mode: 'index', index: 0 }));
    }
  }
  const select = buildSelectPath(segments);
  return select === '' ? undefined : select;
}

/**
 * treeStateToSelect의 변형: 배열 노드가 target이어도 그 배열의 itemSpec(특정 항목)을
 * 직렬화한다. picker '특정 항목' 모드에서 하위 필드를 아직 고르지 않은 상태(배열 자체가
 * target)도 select='<index>'로 안정적으로 표현하기 위함(treeStateToSelect는 target 배열의
 * itemSpec을 무시해 '전체 나열'로 붕괴시킴). itemSpec이 없으면(전체 나열) 그 세그먼트를
 * 빼므로 결과는 동일하게 undefined가 된다. selectToTreeState와 round-trip한다.
 *   - target=배열, itemSpec 없음            → undefined (전체 나열)
 *   - target=배열, itemSpec={index:0}        → '0'        (특정 항목, 항목 전체)
 *   - target=필드, itemSpec={index:0}        → '0.field'  (treeStateToSelect와 동일)
 * 통과(pass-through) 배열은 기존과 동일하게 항목 지정(없으면 index 0)을 부착한다.
 */
export function treeStateToSelectWithItem(
  entry: CatalogEntry,
  targetKey: string,
  itemSpecs: Record<string, ArrayItemSpec>,
): string | undefined {
  const tree = buildCatalogTree(entry);
  const chain = findChain(tree, targetKey) ?? tree;
  const segments: PathSegment[] = [];
  for (const node of chain) {
    const isTarget = node.key === chain[chain.length - 1].key;
    const spec = itemSpecs[node.key];
    if (node.key === ROOT_KEY) {
      if (node.isArray) {
        // 통과: 항목 지정(없으면 index 0). target: itemSpec 있을 때만(없으면 전체 나열).
        if (!isTarget) segments.push(specToSegment(spec ?? { mode: 'index', index: 0 }));
        else if (spec) segments.push(specToSegment(spec));
      }
      continue;
    }
    segments.push({ kind: 'field', name: node.segName });
    if (node.isArray) {
      if (!isTarget) segments.push(specToSegment(spec ?? { mode: 'index', index: 0 }));
      else if (spec) segments.push(specToSegment(spec));
    }
  }
  const select = buildSelectPath(segments);
  return select === '' ? undefined : select;
}

/**
 * 엔진 select 문자열을 트리 선택 상태로 복원(treeStateToSelect의 역, 하위호환 로드).
 * 트리를 따라 내려가며 field로 노드를 이동하고, index/condition은 현재 배열 노드의
 * 항목 지정으로 기록한다. 경로가 트리에서 벗어나면(드리프트) 거기서 멈춘다.
 */
export function selectToTreeState(entry: CatalogEntry, value: string | undefined): TreeState {
  const [root] = buildCatalogTree(entry);
  const segments = parseSelectPath(value);
  const itemSpecs: Record<string, ArrayItemSpec> = {};
  let node = root;
  for (const seg of segments) {
    if (seg.kind === 'field') {
      const child = node.children.find((c) => c.segName === seg.name);
      if (!child) break; // 드리프트 경로 — 현재 노드까지만 복원
      node = child;
      continue;
    }
    // index/condition → 현재(배열) 노드의 항목 지정
    const spec = segmentToSpec(seg);
    if (spec) itemSpecs[node.key] = spec;
  }
  return { targetKey: node.key, itemSpecs };
}

/**
 * 표시 형식의 **보조 자료 출처**(format.scopeFrom) 경로 후보 — 카탈로그에서 스코프로 끌어올 값.
 *
 * rangeMap의 구간 기준표(rangeReturnPaths)와 달리 **형상을 가리지 않는다** — 얹은 값을 무엇으로 쓸지는
 * 형식의 경로 표현식이 정하기 때문이다(구간 배열일 수도, 객체일 수도, 문자열 하나일 수도 있다).
 * 스칼라만 내는 prefillReturnPaths로는 정작 주 용도인 **배열·객체 통째 얹기**를 고를 수 없다.
 *
 * 다만 **배열 아래로는 내려가지 않는다**: rc의 해소(resolveCatalogScopeValue)는 select를 카탈로그
 * 값(배열이면 첫 항목)에 그대로 getByPath로 적용하므로, 항목 지정 없는 `배열.필드` 경로는 읽히지 않는다
 * — 고를 수 있는데 조용히 안 되는 선택지를 만들지 않는다(그런 경로가 필요하면 select 직접 수정이 있다).
 * 객체 아래는 그대로 읽히므로 끝까지 펼친다.
 */
export function scopeReturnPaths(
  entry: CatalogEntry | undefined,
): { path: string; label: string }[] {
  if (!entry) return [];
  const out: { path: string; label: string }[] = [];
  const walk = (fields: CatalogField[], parentPath: string, parentLabel: string): void => {
    for (const f of fields) {
      const path = parentPath ? `${parentPath}.${f.path}` : f.path;
      const label = parentLabel ? `${parentLabel} / ${f.label}` : f.label;
      out.push({ path, label });
      if (f.type === 'object' && (f.fields?.length ?? 0) > 0) walk(f.fields ?? [], path, label);
    }
  };
  // cardinality='list' 엔트리는 returns가 **요소 필드**다 — 해소가 첫 항목에서 읽으므로 그대로 후보가 된다
  // (그래서 이 소비처는 행 인덱스를 고르지 않는다. CatalogPrefillPicker `firstRowOnly` 참조).
  walk(entry.returns, '', '');
  return out;
}

/**
 * 구간 판정(rangeMap)에 **실제로 쓸 수 있는** 배열 필드만 추린 후보 — 구간 기준표(rangesFrom) 선택용.
 *
 * 배열이라고 다 되는 게 아니다: 구간 판정은 `min`/`max`로 값을 가두므로 그 둘이 없는 배열을 고르면
 * **매칭이 없어 배지가 조용히 안 뜬다**(값만 그대로 나온다). 위험성평가 설정만 해도 배열이 넷인데
 * (빈도·강도·수준 판정·상중하) 구간을 가진 건 수준 판정 하나뿐이라, 배열 전체를 후보로 내면 못 쓸
 * 셋을 고를 확률이 더 높고 고른 뒤에 왜 안 되는지 알 방법도 없다.
 *
 * 후보를 좁히는 것은 기능을 가두는 게 아니라 **못 쓸 선택지를 지우는** 것이다 — 새 카탈로그가
 * `{min,max}` 배열을 내면 그 순간 자동으로 후보에 들어온다(선언만으로 확장).
 *
 * 구간 배열은 스칼라 후보(prefillReturnPaths)에는 애초에 나오지 않으므로, 이 목록이 없으면 저작자는
 * 기준표를 고를 수 없어 자유 입력(문자열 경로)으로 내몰린다 — rangesFrom을 만든 이유가 사라진다.
 */
export function rangeReturnPaths(
  entry: CatalogEntry | undefined,
): { path: string; label: string }[] {
  if (!entry) return [];
  return entry.returns
    .filter(
      (r) =>
        r.type === 'array' &&
        (r.fields ?? []).some((f) => f.path === 'min') &&
        (r.fields ?? []).some((f) => f.path === 'max'),
    )
    .map((r) => ({ path: r.path, label: r.label }));
}
